import { Trans } from "@lingui/macro";
import { IApiEpBillingRecurringPayment } from "@smp/api";
import { RefObject, useEffect, useRef, useState } from "react";

import { useCloudpayments } from "@/hooks/use_cloudpayments";
import { gscSendUserParams } from "@/utils";

export const Recurring = ({
  onClose,
  recurringPayment,
  refDialog,
  description = "",
  onReachGoal,
}: {
  onClose: (fireSuccessAction?: boolean) => void;
  onReachGoal: () => void;
  recurringPayment: IApiEpBillingRecurringPayment;
  description?: string;
  refDialog?: RefObject<HTMLDivElement>;
}) => {
  const { isLoaded, pay } = useCloudpayments();

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );

  const node = refDialog?.current;

  const tabIndexesAndElements = useRef<[Element, string | null][]>(
    node
      ? Array.from(node.querySelectorAll("[role] [tabindex]")).map((el) => [
          el,
          el.getAttribute("tabindex"),
        ])
      : [],
  );

  const removeTabIndexes = () => {
    if (tabIndexesAndElements.current) {
      tabIndexesAndElements.current.forEach(([node]) =>
        node.removeAttribute("tabindex"),
      );
    }
  };

  const backTabIndexes = () => {
    if (tabIndexesAndElements.current) {
      tabIndexesAndElements.current.forEach(
        ([node, tabIndex]) =>
          tabIndex && node.setAttribute("tabindex", tabIndex),
      );
    }
  };

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    onReachGoal();

    pay(
      /**
       * Use "auth" for dual confirmation payment scheme (hold) since
       * - CloudPayments has mandatory setup payment
       * - RecurringPayment product cannot be purchased in certain circumstances
       * so we can just cancel the payment instead of refunding.
       */
      "auth",
      {
        accountId: recurringPayment.confirm_widget_args.accountId,
        amount: recurringPayment.confirm_widget_args.amount,
        currency: recurringPayment.confirm_widget_args.currency,
        data: recurringPayment.confirm_widget_args.data,
        description,
        invoiceId: String(recurringPayment.confirm_widget_args.invoiceId),
        publicId: recurringPayment.confirm_widget_args.publicId,
        skin: "modern",
      },
      {
        onComplete: () => {
          // WARNING: onComplete fired when user cant pass not robot verification
          // ensure we dont need to tab indexes on some other fires
        },
        onFail: (errorMessage) => {
          backTabIndexes();
          setErrorMessage(errorMessage);
          gscSendUserParams({ is_payment_canceled: true });
        },
        onSuccess: () => {
          backTabIndexes();
          onClose(true);
        },
      },
    );

    removeTabIndexes();
  }, [description, isLoaded, onClose, onReachGoal, pay, recurringPayment]);

  if (errorMessage) {
    return <Trans>It looks like you canceled the payment!</Trans>;
  }

  return <Trans>Payment in progress, do not close this window...</Trans>;
};
